import { Button, Dialog, Input, VSpacer } from '@/components/DesignSystem';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { Stack } from '@mui/material';
import React, { useState } from 'react';
import { ApiTwilioConfig } from "@api/interfaces/ApiTwilioConfig";
import { TwilioConfigApi } from "@/utilities/api/TwilioConfigApi";
import { isEmpty } from "lodash";

interface SaveTwilioDetailsModalProps {
  existingTwilioConfig: ApiTwilioConfig | null,
  onClose: () => void,
  onSave: () => void,
  retailerId: string,
}

export const SaveTwilioDetailsModal = ({
  existingTwilioConfig,
  onClose,
  onSave,
  retailerId,
}: SaveTwilioDetailsModalProps) => {
  const [accountSid, setAccountSid] = useState(existingTwilioConfig?.accountSid);
  const [apiKey, setApiKey] = useState(existingTwilioConfig?.apiKey);
  const [apiSecret, setApiSecret] = useState(existingTwilioConfig?.apiSecret);
  const [messagingServiceSid, setMessagingServiceSid] =
    useState(existingTwilioConfig?.messagingServiceSid);
  const [messagingTelephone, setMessagingTelephone] =
    useState(existingTwilioConfig?.messagingTelephone);
  const [authToken, setAuthToken] = useState(existingTwilioConfig?.authToken);

  const [editedFields, setEditedFields] = useState<{ [key: string]: boolean }>({});

  const [isSaving, setIsSaving] = useState(false);
  const { openSnackbar } = useSnackbar();
  const isFormValid = !isEmpty(editedFields) && !!(
    (editedFields.accountSid ? accountSid : existingTwilioConfig?.accountSid)
    && (editedFields.apiKey ? apiKey : existingTwilioConfig?.apiKey)
    && (editedFields.apiSecret ? apiSecret : existingTwilioConfig?.apiSecret)
    && (editedFields.authToken ? authToken : existingTwilioConfig?.authToken)
    && (
      editedFields.messagingServiceSid
        ? messagingServiceSid
        : existingTwilioConfig?.messagingServiceSid
    )
    && (
      editedFields.messagingTelephone
        ? messagingTelephone
        : existingTwilioConfig?.messagingTelephone
    )
  );

  const handleFieldFocus = (field: keyof ApiTwilioConfig, setValue: (v: string) => void) => {
    if (!editedFields[field]) {
      setValue('');
      setEditedFields((prev) => ({ ...prev, [field]: true }));
    }
  };

  const handleFieldChange = (
    field: keyof ApiTwilioConfig,
    setValue: (v: string) => void,
  ) => (v: string) => {
    setValue(v);
    setEditedFields((prev) => ({ ...prev, [field]: true }));
  };

  const getOriginalValue = (
    field: keyof ApiTwilioConfig,
  ): string | undefined | null => {
    return existingTwilioConfig?.[field];
  };

  const handleFieldBlur = (
    field: keyof ApiTwilioConfig,
    value: string | undefined | null,
    setValue: (v: string) => void,
  ) => {
    if (editedFields[field] && (!value
      || value.trim() === ''
      || value === getOriginalValue(field))
    ) {
      const originalValue = getOriginalValue(field);
      setValue(originalValue || '');
      setEditedFields((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleSave = async () => {
    if (!isFormValid) {
      return;
    }
    setIsSaving(true);
    try {
      const payload: Record<string, string> = {};
      if (existingTwilioConfig) {
        if (editedFields.accountSid && accountSid !== undefined) {
          payload.accountSid = accountSid;
        }
        if (editedFields.apiKey && apiKey !== undefined) {
          payload.apiKey = apiKey;
        }
        if (editedFields.apiSecret && apiSecret !== undefined) {
          payload.apiSecret = apiSecret;
        }
        if (editedFields.authToken && !!authToken) {
          payload.authToken = authToken;
        }
        if (editedFields.messagingServiceSid && messagingServiceSid !== undefined) {
          payload.messagingServiceSid = messagingServiceSid;
        }
        if (editedFields.messagingTelephone && messagingTelephone !== undefined) {
          payload.messagingTelephone = messagingTelephone;
        }
        await TwilioConfigApi.update(existingTwilioConfig.id, payload);
      } else {
        await TwilioConfigApi.createForRetailer(retailerId, {
          accountSid: accountSid!,
          apiKey: apiKey!,
          apiSecret: apiSecret!,
          authToken: authToken!,
          messagingServiceSid: messagingServiceSid!,
          messagingTelephone: messagingTelephone!,
        });
      }
      openSnackbar(`Twilio config successfully ${existingTwilioConfig ? 'updated' : 'created'}`);
      onSave();
      onClose();
    } catch (e) {
      openSnackbar(e instanceof Error ? e.message : String(e));
    }
    setIsSaving(false);
  };

  return (
    <Dialog
      acceptButton={(props) => (
        <Button
          {...props}
          disabled={!isFormValid}
          loading={isSaving}
          onClick={handleSave}
          variant="contained"
        >
          Save
        </Button>
      )}
      cancelButton={(props) => (
        <Button {...props} onClick={onClose}>
          {existingTwilioConfig ? 'Close' : 'Cancel'}
        </Button>
      )}
      onClose={onClose}
      open
      showCloseButton={false}
      testID="save-twilio-details-modal"
      title="Twilio details"
    >
      <Stack>
        <VSpacer size="5" />
        <Input
          label="Account SID"
          onBlur={() => handleFieldBlur('accountSid', accountSid, setAccountSid)}
          onChangeText={handleFieldChange('accountSid', setAccountSid)}
          onFocus={() => handleFieldFocus('accountSid', setAccountSid)}
          testID="account-sid-input"
          value={accountSid}
        />
        <VSpacer size="5" />
        <Input
          label="API Key SID"
          onBlur={() => handleFieldBlur('apiKey', apiKey, setApiKey)}
          onChangeText={handleFieldChange('apiKey', setApiKey)}
          onFocus={() => handleFieldFocus('apiKey', setApiKey)}
          testID="api-key-sid-input"
          value={apiKey}
        />
        <VSpacer size="5" />
        <Input
          label="API Key Secret"
          onBlur={() => handleFieldBlur('apiSecret', apiSecret, setApiSecret)}
          onChangeText={handleFieldChange('apiSecret', setApiSecret)}
          onFocus={() => handleFieldFocus('apiSecret', setApiSecret)}
          testID="api-key-secret-input"
          value={apiSecret}
        />
        <VSpacer size="5" />
        <Input
          label="Auth Token"
          onBlur={() => handleFieldBlur('authToken', authToken, setAuthToken)}
          onChangeText={handleFieldChange('authToken', setAuthToken)}
          onFocus={() => handleFieldFocus('authToken', setAuthToken)}
          testID="auth-token-input"
          value={authToken}
        />
        <VSpacer size="5" />
        <Input
          label="Messaging Service SID"
          onBlur={() =>
            handleFieldBlur(
              'messagingServiceSid',
              messagingServiceSid,
              setMessagingServiceSid,
            )
          }
          onChangeText={handleFieldChange('messagingServiceSid', setMessagingServiceSid)}
          onFocus={() => handleFieldFocus('messagingServiceSid', setMessagingServiceSid)}
          testID="messaging-service-sid-input"
          value={messagingServiceSid}
        />
        <VSpacer size="5" />
        <Input
          label="Messaging Telephone"
          onChangeText={handleFieldChange('messagingTelephone', setMessagingTelephone)}
          testID="messaging-telephone-input"
          value={messagingTelephone}
        />
      </Stack>
    </Dialog>
  );
};
